import {
  Accordion,
  ActionIcon,
  Badge,
  Button,
  Checkbox,
  Divider,
  Drawer,
  FileButton,
  Group,
  Loader,
  Modal,
  NumberInput,
  Pagination,
  ScrollArea,
  Select,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
  IconAdjustments,
  IconAlertCircle,
  IconAlertTriangle,
  IconBook,
  IconChevronRight,
  IconDownload,
  IconFileText,
  IconHistory,
  IconLink,
  IconShieldCheck,
  IconTrash,
  IconUpload,
  IconX,
} from '@tabler/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CustomDatePicker } from '../../components/custom-date-picker';
import { EpiCatalogPanel } from '../../components/epi-catalog-panel';
import { EpiTutorial } from '../../components/epi-tutorial';
import { Page } from '../../components/page';
import {
  exportEpiHistoryReceiptRequest,
  exportEpiReceiptRequest,
} from '../../data/exportables';
import {
  useAddEmployeeFiles,
  useEpiCatalog,
  useLinkEpis,
  useListEpiDeadlines,
  useListEpiDeliveries,
  useListEpisGrouped,
  useMarkEpiAsDelivered,
  useUnlinkEpi,
} from '../../data/hooks/employee';
import { useUploadMultipleFiles } from '../../data/hooks/files';
import { useGetUser } from '../../data/hooks/user';
import {
  EpiDeadlineItem,
  EpiDeliveryReasonLabel,
  EpiDeliveryReasonOptions,
  GroupedEpiRecord,
} from '../../models/employees';
import { UserRole } from '../../models/user';
import { dayjsPlugins, formatLocale } from '../../providers/dayjs-plugins';
import { successNotification } from '../../providers/mantine-notifications';
import { SECTOR_OPTIONS } from '../../utils/constants/sectors';
import { userRoleHumanized, userRoleList } from '../../utils/constants/user';

const EMPLOYEES_PER_PAGE = 7;

type EpiStatus = 'entregue' | 'pendente' | 'atrasado' | 'sem-prazo';

const STATUS_OPTIONS = [
  { value: 'entregue', label: 'Entregue' },
  { value: 'pendente', label: 'Pendente de entrega' },
  { value: 'atrasado', label: 'Atrasado / necessita reposição' },
  { value: 'sem-prazo', label: 'Sem prazo definido' },
];

function StatusBadge({ status }: { status: EpiStatus }) {
  const map: Record<EpiStatus, { color: string; label: string }> = {
    entregue: { color: 'green', label: 'Entregue' },
    pendente: { color: 'yellow', label: 'Pendente' },
    atrasado: { color: 'red', label: 'Atrasado' },
    'sem-prazo': { color: 'gray', label: 'Sem prazo' },
  };
  const { color, label } = map[status];
  return (
    <Badge color={color} variant="filled" size="sm">
      {label}
    </Badge>
  );
}

function computeStatus(record: {
  lastDeliveredAt?: string | Date | null;
  epi: { replacementPeriodDays?: number | null; controlType?: string | null };
}): EpiStatus {
  const ct = record.epi.controlType;
  if (ct === 'descartavel') {
    return record.lastDeliveredAt ? 'entregue' : 'pendente';
  }
  if (ct === 'por_condicao') return 'sem-prazo';
  // reutilizavel (padrão)
  if (!record.lastDeliveredAt) return 'pendente';
  const replacementDays = record.epi.replacementPeriodDays;
  if (!replacementDays) return 'sem-prazo';
  const daysSince = dayjsPlugins().diff(
    dayjsPlugins(record.lastDeliveredAt),
    'day',
  );
  return daysSince > replacementDays ? 'atrasado' : 'entregue';
}

export function EpiList() {
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('geral');

  // --- Visão Geral ---
  const {
    fetch: listEpisGroupedFetcher,
    response: groupedData,
    loading: listLoading,
  } = useListEpisGrouped();

  // --- Pendentes ---
  const {
    fetch: listPendentesFetcher,
    response: pendentesData,
    loading: pendentesLoading,
  } = useListEpisGrouped();
  const [pendentesPage, setPendentesPage] = useState(1);
  const pendentesLoaded = useRef(false);

  // --- Prazos ---
  const {
    fetch: listDeadlinesFetcher,
    response: deadlinesData,
    loading: deadlinesLoading,
  } = useListEpiDeadlines();
  const deadlinesLoaded = useRef(false);

  const { list: listCatalogFetcher, catalog } = useEpiCatalog();
  const { fetch: listUsersFetcher, response: users } = useGetUser();
  const [usersLoaded, setUsersLoaded] = useState(false);

  const { deliver: markAsDelivered, loading: deliverLoading } =
    useMarkEpiAsDelivered();
  const { link: linkEpi, loading: linkLoading } = useLinkEpis();
  const { unlink: unlinkEpi } = useUnlinkEpi();
  const { fetch: uploadFilesFetcher, loading: uploadFilesLoading } =
    useUploadMultipleFiles();
  const { fetch: addEmployeeFilesFetcher, loading: addEmployeeFilesLoading } =
    useAddEmployeeFiles();

  const [employeePage, setEmployeePage] = useState(1);

  const formFilter = useForm({
    initialValues: {
      employeeId: '',
      epiId: '',
      sector: '',
      status: '',
      role: '',
      noEpi: false,
      dateBegin: null as Date | null,
      dateEnd: null as Date | null,
    },
  });

  // Server-side filter state
  const [filterEmployeeId, setFilterEmployeeId] = useState<string | null>(null);
  const [filterEpiId, setFilterEpiId] = useState<string | null>(null);
  const [filterSector, setFilterSector] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [filterRole, setFilterRole] = useState<string | null>(null);
  const [filterNoEpi, setFilterNoEpi] = useState(false);
  const [filterStartDate, setFilterStartDate] = useState<Date | null>(null);
  const [filterEndDate, setFilterEndDate] = useState<Date | null>(null);

  const [deliverModalId, setDeliverModalId] = useState<number | null>(null);
  const [termoModal, setTermoModal] = useState<{
    employeeId: number;
    employeeName: string;
    pdfUrl: string | null;
    blob: Blob | null;
    loading: boolean;
  } | null>(null);
  const [historyModal, setHistoryModal] = useState<{
    employeeId: number;
    employeeName: string;
  } | null>(null);
  const {
    fetch: fetchDeliveries,
    response: deliveries,
    loading: deliveriesLoading,
  } = useListEpiDeliveries();
  const [linkModal, setLinkModal] = useState<{
    employeeId: number;
    linkedEpiIds: number[];
  } | null>(null);
  const [linkEpiId, setLinkEpiId] = useState<string | null>(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const deliverForm = useForm({
    initialValues: {
      reason: '',
      quantity: 1,
      deliveredAt: new Date() as Date | null,
    },
  });

  function buildServerQuery(overrides?: { page?: number }) {
    return {
      employeeId: filterEmployeeId ? Number(filterEmployeeId) : undefined,
      epiId: filterEpiId ? Number(filterEpiId) : undefined,
      sector: filterSector || undefined,
      role: filterRole || undefined,
      hasNoEpi: filterNoEpi || undefined,
      startDate: filterStartDate ? filterStartDate.toISOString() : undefined,
      endDate: filterEndDate ? filterEndDate.toISOString() : undefined,
      page: overrides?.page ?? employeePage,
      limit: EMPLOYEES_PER_PAGE,
    };
  }

  function loadData() {
    listEpisGroupedFetcher(buildServerQuery());
    if (pendentesLoaded.current) {
      listPendentesFetcher({
        page: pendentesPage,
        limit: EMPLOYEES_PER_PAGE,
        hasPendingDelivery: true,
      });
    }
  }

  useEffect(() => {
    listEpisGroupedFetcher({ page: 1, limit: EMPLOYEES_PER_PAGE });
  }, []);

  function handleTabChange(value: string) {
    setActiveTab(value);
    if (value === 'prazos' && !deadlinesLoaded.current) {
      listDeadlinesFetcher();
      deadlinesLoaded.current = true;
    }
    if (value === 'pendentes' && !pendentesLoaded.current) {
      listPendentesFetcher({
        page: 1,
        limit: EMPLOYEES_PER_PAGE,
        hasPendingDelivery: true,
      });
      pendentesLoaded.current = true;
    }
  }

  function handleFilter(values: typeof formFilter.values) {
    const newEmployeeId = values.employeeId || null;
    const newEpiId = values.epiId || null;
    const newSector = values.sector || null;
    const newStatus = values.status || null;
    const newRole = values.role || null;
    const newNoEpi = values.noEpi;
    const newStartDate = values.dateBegin;
    const newEndDate = values.dateEnd;

    setFilterEmployeeId(newEmployeeId);
    setFilterEpiId(newEpiId);
    setFilterSector(newSector);
    setFilterStatus(newStatus);
    setFilterRole(newRole);
    setFilterNoEpi(newNoEpi);
    setFilterStartDate(newStartDate);
    setFilterEndDate(newEndDate);
    setEmployeePage(1);
    setFilterDrawerOpen(false);

    listEpisGroupedFetcher({
      employeeId: newEmployeeId ? Number(newEmployeeId) : undefined,
      epiId: newEpiId ? Number(newEpiId) : undefined,
      sector: newSector || undefined,
      role: newRole || undefined,
      hasNoEpi: newNoEpi || undefined,
      startDate: newStartDate ? newStartDate.toISOString() : undefined,
      endDate: newEndDate ? newEndDate.toISOString() : undefined,
      page: 1,
      limit: EMPLOYEES_PER_PAGE,
    });
  }

  function handleClearFilter() {
    formFilter.reset();
    setFilterEmployeeId(null);
    setFilterEpiId(null);
    setFilterSector(null);
    setFilterStatus(null);
    setFilterRole(null);
    setFilterNoEpi(false);
    setFilterStartDate(null);
    setFilterEndDate(null);
    setEmployeePage(1);
    listEpisGroupedFetcher({ page: 1, limit: EMPLOYEES_PER_PAGE });
  }

  const visibleEmployees = useMemo(() => {
    if (!groupedData?.items) return [];
    if (!filterStatus) return groupedData.items;
    return groupedData.items.map((emp) => ({
      ...emp,
      epis: emp.epis.filter((r) => computeStatus(r) === filterStatus),
    }));
  }, [groupedData, filterStatus]);

  const totalEmployeePages = groupedData?.meta.totalPages ?? 1;
  const totalEmployees = groupedData?.meta.totalItems ?? 0;

  const pendentesEmployees = pendentesData?.items ?? [];
  const totalPendentesPages = pendentesData?.meta.totalPages ?? 1;
  const totalPendentes = pendentesData?.meta.totalItems ?? 0;

  // Deadline grouping
  const deadlineItems = deadlinesData?.items ?? [];
  const overdue = deadlineItems.filter((i) => i.daysUntilDue < 0);
  const thisWeek = deadlineItems.filter(
    (i) => i.daysUntilDue >= 0 && i.daysUntilDue <= 7,
  );
  const thisMonth = deadlineItems.filter(
    (i) => i.daysUntilDue >= 8 && i.daysUntilDue <= 30,
  );

  async function handleDeliver(
    id: number,
    body: { reason?: string; quantity?: number; deliveredAt?: Date | null },
  ) {
    await markAsDelivered(
      id,
      () => {
        successNotification({
          title: 'Sucesso',
          message: 'Entrega de EPI registrada com sucesso',
        });
        setDeliverModalId(null);
        deliverForm.reset();
        loadData();
      },
      {
        reason: body.reason,
        quantity: body.quantity,
        deliveredAt: body.deliveredAt
          ? body.deliveredAt.toISOString()
          : undefined,
      },
    );
  }

  function handleLinkEpi() {
    if (!linkModal || !linkEpiId) return;
    linkEpi(
      { employeeIds: [linkModal.employeeId], epiIds: [Number(linkEpiId)] },
      () => {
        successNotification({ title: 'EPI vinculado!', message: '' });
        setLinkModal(null);
        setLinkEpiId(null);
        loadData();
      },
    );
  }

  function handleUnlinkEpi(recordId: number) {
    unlinkEpi(recordId, () => {
      successNotification({ title: 'EPI desvinculado.', message: '' });
      loadData();
    });
  }

  function downloadFile(data: Blob, fileName: string) {
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function handleUploadSignedTermo(
    file: File,
    employeeId: number,
  ): Promise<void> {
    let fileIds: number[] = [];
    await uploadFilesFetcher({
      data: { files: [file] },
      onSuccess: (ids) => {
        fileIds = ids;
      },
    });
    if (!fileIds.length) return;
    await addEmployeeFilesFetcher({
      employeeId,
      data: { files: fileIds, category: 'epi' },
      onSuccess: () => {
        successNotification({
          title: 'Termo assinado enviado!',
          message: 'O arquivo foi salvo nos documentos do funcionário.',
        });
      },
    });
  }

  async function handleOpenTermo(employeeId: number, employeeName: string) {
    setTermoModal({
      employeeId,
      employeeName,
      pdfUrl: null,
      blob: null,
      loading: true,
    });
    try {
      const response = await exportEpiReceiptRequest(employeeId);
      const blob: Blob = response.data;
      const pdfUrl = URL.createObjectURL(blob);
      setTermoModal({ employeeId, employeeName, pdfUrl, blob, loading: false });
    } catch {
      setTermoModal(null);
    }
  }

  function handleCloseTermoModal() {
    if (termoModal?.pdfUrl) URL.revokeObjectURL(termoModal.pdfUrl);
    setTermoModal(null);
  }

  function handleOpenHistorico(employeeId: number, employeeName: string) {
    setHistoryModal({ employeeId, employeeName });
    fetchDeliveries({ employeeId, limit: 100 });
  }

  async function handleDownloadHistorico(employeeId: number) {
    try {
      const response = await exportEpiHistoryReceiptRequest(employeeId);
      downloadFile(response.data, `historico-epi-${employeeId}.pdf`);
    } catch {
      // error handled by axios interceptor
    }
  }

  const activeFilterCount = [
    filterEmployeeId,
    filterEpiId,
    filterSector,
    filterStatus,
    filterRole,
    filterStartDate,
    filterEndDate,
    filterNoEpi || null,
  ].filter(Boolean).length;

  const filterEmployeeOptions = (users ?? [])
    .filter(
      (u) =>
        u.employee?.id &&
        ![
          UserRole.NONE,
          UserRole.MANAGER_VIEWER,
          UserRole.MANAGER_CREATOR,
        ].includes(u.role),
    )
    .map((u) => ({ value: String(u.employee?.id), label: u.username }));

  const filterEpiOptions = (catalog ?? []).map((epi) => ({
    value: String(epi.id),
    label: epi.name,
  }));

  // Reusable accordion JSX for employee list
  function renderEmployeeAccordion(
    employees: typeof visibleEmployees,
    totalPages: number,
    currentPage: number,
    onPageChange: (p: number) => void,
    isLoading: boolean,
    totalCount: number,
    emptyMessage?: string,
  ) {
    if (isLoading) {
      return (
        <Group position="center" py={48}>
          <Loader />
        </Group>
      );
    }
    if (employees.length === 0) {
      return (
        <Stack align="center" py={48} spacing={8}>
          <IconShieldCheck size={40} color="#adb5bd" />
          <Text fw="bold" color="dimmed">
            Nenhum funcionário encontrado
          </Text>
          <Text size="sm" color="dimmed">
            {emptyMessage ??
              'Ajuste os filtros para visualizar os funcionários.'}
          </Text>
        </Stack>
      );
    }
    return (
      <>
        {totalCount > 0 && (
          <Group spacing={16} mb={8}>
            <Text size="sm" color="dimmed">
              <Text span fw="bold" color="dark">
                {totalCount}
              </Text>{' '}
              funcionário{totalCount !== 1 ? 's' : ''}
            </Text>
          </Group>
        )}
        <Accordion variant="separated" chevron={<IconChevronRight size={16} />}>
          {employees.map(({ id, employeeName, sector, role, epis }) => {
            const statuses = epis.map((r) => computeStatus(r));
            const hasAtrasado = statuses.includes('atrasado');
            const hasPendente = statuses.includes('pendente');
            const atrasadoCount = statuses.filter(
              (s) => s === 'atrasado',
            ).length;
            const pendenteCount = statuses.filter(
              (s) => s === 'pendente',
            ).length;

            return (
              <Accordion.Item
                key={id}
                value={String(id)}
                sx={(theme) => ({
                  border: `1px solid ${theme.colors.gray[3]}`,
                  borderRadius: theme.radius.sm,
                })}
              >
                <Accordion.Control>
                  <Group position="apart" pr={8}>
                    <div>
                      <Group spacing={8} mb={2}>
                        <Text fw={600}>{employeeName}</Text>
                      </Group>
                      <Group spacing={6}>
                        {role && (
                          <Text size="xs" color="dimmed">
                            {(userRoleHumanized as Record<string, string>)[
                              role
                            ] ?? role}
                          </Text>
                        )}
                        {sector && (
                          <>
                            <Text size="xs" color="dimmed">
                              ·
                            </Text>
                            <Text size="xs" color="dimmed">
                              {sector}
                            </Text>
                          </>
                        )}
                      </Group>
                    </div>
                    <Group spacing={8}>
                      {epis.length === 0 ? (
                        <Badge color="gray" variant="light" size="sm">
                          Sem EPI vinculado
                        </Badge>
                      ) : (
                        <>
                          <Badge color="blue" variant="light" size="sm">
                            {epis.length} EPI
                            {epis.length !== 1 ? 's' : ''}
                          </Badge>
                          {hasAtrasado && (
                            <Badge color="red" variant="filled" size="sm">
                              {atrasadoCount} atrasado
                              {atrasadoCount !== 1 ? 's' : ''}
                            </Badge>
                          )}
                          {hasPendente && !hasAtrasado && (
                            <Badge color="yellow" variant="filled" size="sm">
                              {pendenteCount} pendente
                              {pendenteCount !== 1 ? 's' : ''}
                            </Badge>
                          )}
                          {!hasAtrasado && !hasPendente && (
                            <Badge color="green" variant="filled" size="sm">
                              Em dia
                            </Badge>
                          )}
                        </>
                      )}
                    </Group>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Stack spacing={8}>
                    <Group position="right" mb={4} spacing={8}>
                      <Tooltip label="Visualizar Termo de EPI" withArrow>
                        <Button
                          size="xs"
                          variant="subtle"
                          color="gray"
                          leftIcon={<IconFileText size={13} />}
                          onClick={() => handleOpenTermo(id, employeeName)}
                        >
                          Termo
                        </Button>
                      </Tooltip>
                      <Tooltip label="Histórico de Entregas" withArrow>
                        <Button
                          size="xs"
                          variant="subtle"
                          color="gray"
                          leftIcon={<IconHistory size={13} />}
                          onClick={() => handleOpenHistorico(id, employeeName)}
                        >
                          Histórico
                        </Button>
                      </Tooltip>
                      <Button
                        size="xs"
                        variant="light"
                        color="teal"
                        leftIcon={<IconLink size={13} />}
                        onClick={() => {
                          setLinkModal({
                            employeeId: id,
                            linkedEpiIds: epis.map((r) => r.epi.id),
                          });
                          setLinkEpiId(null);
                          if (!catalog?.length) listCatalogFetcher();
                        }}
                      >
                        Vincular EPI
                      </Button>
                    </Group>
                    {epis.length === 0 ? (
                      <Text size="sm" color="dimmed" align="center" py={16}>
                        Nenhum EPI vinculado a este funcionário.
                      </Text>
                    ) : (
                      epis.map((record: GroupedEpiRecord, rIdx: number) => {
                        const status = statuses[rIdx];
                        const lastDate = record.lastDeliveredAt ?? null;
                        return (
                          <Group
                            key={record.id}
                            position="apart"
                            px={12}
                            py={10}
                            sx={(theme) => ({
                              border: `1px solid ${theme.colors.gray[3]}`,
                              borderRadius: theme.radius.sm,
                              backgroundColor: theme.white,
                            })}
                          >
                            <div>
                              <Group spacing={8} mb={2}>
                                <Text size="sm" fw={500}>
                                  {record.epi?.name ?? '—'}
                                </Text>
                                {record.epi?.category && (
                                  <Badge color="blue" variant="light" size="xs">
                                    {record.epi.category}
                                  </Badge>
                                )}
                              </Group>
                              {(record.epi?.brand || record.epi?.ca) && (
                                <Text size="xs" color="dimmed">
                                  {[
                                    record.epi?.brand,
                                    record.epi?.ca
                                      ? `CA ${record.epi.ca}`
                                      : null,
                                  ]
                                    .filter(Boolean)
                                    .join(' · ')}
                                </Text>
                              )}
                              <Text
                                size="xs"
                                color={lastDate ? 'teal' : 'dimmed'}
                                fw={lastDate ? 500 : undefined}
                              >
                                {lastDate
                                  ? `Entregue em ${formatLocale(
                                      lastDate,
                                      'DD/MM/YYYY',
                                    )}`
                                  : 'Sem entrega registrada'}
                              </Text>
                            </div>
                            <Group spacing={8}>
                              <StatusBadge status={status} />
                              <Button
                                size="xs"
                                variant="light"
                                color="blue"
                                onClick={() => setDeliverModalId(record.id)}
                              >
                                Marcar Entregue
                              </Button>
                              <Tooltip label="Desvincular EPI" withArrow>
                                <ActionIcon
                                  size="sm"
                                  variant="subtle"
                                  color="red"
                                  onClick={() => handleUnlinkEpi(record.id)}
                                >
                                  <IconTrash size={14} />
                                </ActionIcon>
                              </Tooltip>
                            </Group>
                          </Group>
                        );
                      })
                    )}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            );
          })}
        </Accordion>
        {totalPages > 1 && (
          <Group position="center" mt={8}>
            <Pagination
              total={totalPages}
              page={currentPage}
              onChange={(p) => {
                onPageChange(p);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            />
          </Group>
        )}
      </>
    );
  }

  function renderDeadlineSection(
    items: EpiDeadlineItem[],
    label: string,
    color: string,
  ) {
    if (items.length === 0) return null;
    return (
      <Stack spacing={8} mb={16}>
        <Text fw={700} size="sm" color={color}>
          {label}
        </Text>
        {items.map((item) => (
          <Group
            key={`${item.linkId}`}
            position="apart"
            px={12}
            py={10}
            sx={(theme) => ({
              border: `1px solid ${theme.colors.gray[3]}`,
              borderRadius: theme.radius.sm,
              backgroundColor: theme.white,
            })}
          >
            <div>
              <Text size="sm" fw={600}>
                {item.employeeName}
              </Text>
              <Text size="xs" color="dimmed">
                {[item.sector, item.epiName].filter(Boolean).join(' · ')}
              </Text>
              <Text size="xs" color="dimmed">
                Última entrega:{' '}
                {formatLocale(item.lastDeliveredAt, 'DD/MM/YYYY')}
              </Text>
            </div>
            <div style={{ textAlign: 'right' }}>
              <Badge
                color={
                  item.daysUntilDue < 0
                    ? 'red'
                    : item.daysUntilDue <= 7
                    ? 'orange'
                    : 'blue'
                }
              >
                {item.daysUntilDue < 0
                  ? `${Math.abs(item.daysUntilDue)} dias em atraso`
                  : `${item.daysUntilDue} dias restantes`}
              </Badge>
              <Text size="xs" color="dimmed" mt={4}>
                Vence em {formatLocale(item.nextDueDate, 'DD/MM/YYYY')}
              </Text>
            </div>
          </Group>
        ))}
      </Stack>
    );
  }

  return (
    <Page>
      <Stack spacing={12}>
        <Group position="apart" align="flex-start">
          <div>
            <Title>Gestão de EPIs</Title>
            <Text color="gray" size="sm">
              Equipamentos de Proteção Individual vinculados aos funcionários
            </Text>
          </div>
          <Group spacing={8} align="center">
            <EpiTutorial screen="list" />
            <Button
              variant="light"
              color="gray"
              leftIcon={<IconBook size={16} />}
              onClick={() => setCatalogOpen(true)}
            >
              Catálogo
            </Button>
            {activeTab === 'geral' && (
              <Button
                variant="light"
                leftIcon={<IconAdjustments size={16} />}
                onClick={() => {
                  setFilterDrawerOpen(true);
                  if (!catalog?.length) listCatalogFetcher();
                  if (!usersLoaded) {
                    listUsersFetcher();
                    setUsersLoaded(true);
                  }
                }}
              >
                Filtros
                {activeFilterCount > 0 && (
                  <Badge color="blue" variant="filled" size="xs" ml={6}>
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            )}
          </Group>
        </Group>

        <Group position="left">
          <Tabs value={activeTab} onTabChange={handleTabChange} variant="pills">
            <Tabs.List>
              <Tabs.Tab value="geral">Visão Geral</Tabs.Tab>
              <Tabs.Tab value="prazos" icon={<IconAlertTriangle size={14} />}>
                Prazos
              </Tabs.Tab>
              <Tabs.Tab value="pendentes" icon={<IconAlertCircle size={14} />}>
                Pendentes
              </Tabs.Tab>
            </Tabs.List>
          </Tabs>
        </Group>

        <Drawer
          opened={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          title="Filtros"
          position="right"
          size="xl"
          padding="xl"
        >
          <form onSubmit={formFilter.onSubmit(handleFilter)}>
            <Stack spacing={12}>
              <Select
                label="Funcionário"
                placeholder="selecione o funcionário"
                clearable
                searchable
                data={filterEmployeeOptions}
                {...formFilter.getInputProps('employeeId')}
              />
              <Select
                label="Cargo"
                placeholder="selecione o cargo"
                clearable
                data={userRoleList}
                {...formFilter.getInputProps('role')}
              />
              <Select
                label="Setor"
                placeholder="selecione o setor"
                clearable
                searchable
                data={SECTOR_OPTIONS}
                {...formFilter.getInputProps('sector')}
              />
              <Select
                label="EPI"
                placeholder="selecione o EPI"
                clearable
                searchable
                data={filterEpiOptions}
                {...formFilter.getInputProps('epiId')}
              />
              <Select
                label="Status"
                placeholder="selecione o status"
                clearable
                data={STATUS_OPTIONS}
                {...formFilter.getInputProps('status')}
              />
              <CustomDatePicker
                label="Data Inicial"
                placeholder="selecione uma data"
                {...formFilter.getInputProps('dateBegin')}
              />
              <CustomDatePicker
                label="Data Final"
                placeholder="selecione uma data"
                {...formFilter.getInputProps('dateEnd')}
              />
              <Checkbox
                label="Sem EPI vinculado"
                mt={4}
                {...formFilter.getInputProps('noEpi', { type: 'checkbox' })}
              />
              <Group position="right" mt={8}>
                <Button
                  variant="outline"
                  leftIcon={<IconX size={14} />}
                  onClick={handleClearFilter}
                >
                  Limpar
                </Button>
                <Button type="submit">Aplicar</Button>
              </Group>
            </Stack>
          </form>
        </Drawer>

        <Tabs value={activeTab} onTabChange={handleTabChange} variant="pills">
          {/* -------- VISÃO GERAL -------- */}
          <Tabs.Panel value="geral" pt={16}>
            {!listLoading && totalEmployees > 0 && (
              <Group spacing={16} mb={8}>
                <Text size="sm" color="dimmed">
                  <Text span fw="bold" color="dark">
                    {totalEmployees}
                  </Text>{' '}
                  funcionário{totalEmployees !== 1 ? 's' : ''}
                </Text>
              </Group>
            )}
            {listLoading ? (
              <Group position="center" py={48}>
                <Loader />
              </Group>
            ) : visibleEmployees.length === 0 ? (
              <Stack align="center" py={48} spacing={8}>
                <IconShieldCheck size={40} color="#adb5bd" />
                <Text fw="bold" color="dimmed">
                  Nenhum funcionário encontrado
                </Text>
                <Text size="sm" color="dimmed">
                  Ajuste os filtros para visualizar os funcionários.
                </Text>
              </Stack>
            ) : (
              <>
                <Accordion
                  variant="separated"
                  chevron={<IconChevronRight size={16} />}
                >
                  {visibleEmployees.map(
                    ({ id, employeeName, sector, role, epis }) => {
                      const statuses = epis.map((r) => computeStatus(r));
                      const hasAtrasado = statuses.includes('atrasado');
                      const hasPendente = statuses.includes('pendente');
                      const atrasadoCount = statuses.filter(
                        (s) => s === 'atrasado',
                      ).length;
                      const pendenteCount = statuses.filter(
                        (s) => s === 'pendente',
                      ).length;

                      return (
                        <Accordion.Item
                          key={id}
                          value={String(id)}
                          sx={(theme) => ({
                            border: `1px solid ${theme.colors.gray[3]}`,
                            borderRadius: theme.radius.sm,
                          })}
                        >
                          <Accordion.Control>
                            <Group position="apart" pr={8}>
                              <div>
                                <Group spacing={8} mb={2}>
                                  <Text fw={600}>{employeeName}</Text>
                                </Group>
                                <Group spacing={6}>
                                  {role && (
                                    <Text size="xs" color="dimmed">
                                      {(
                                        userRoleHumanized as Record<
                                          string,
                                          string
                                        >
                                      )[role] ?? role}
                                    </Text>
                                  )}
                                  {sector && (
                                    <>
                                      <Text size="xs" color="dimmed">
                                        ·
                                      </Text>
                                      <Text size="xs" color="dimmed">
                                        {sector}
                                      </Text>
                                    </>
                                  )}
                                </Group>
                              </div>
                              <Group spacing={8}>
                                {epis.length === 0 ? (
                                  <Badge color="gray" variant="light" size="sm">
                                    Sem EPI vinculado
                                  </Badge>
                                ) : (
                                  <>
                                    <Badge
                                      color="blue"
                                      variant="light"
                                      size="sm"
                                    >
                                      {epis.length} EPI
                                      {epis.length !== 1 ? 's' : ''}
                                    </Badge>
                                    {hasAtrasado && (
                                      <Badge
                                        color="red"
                                        variant="filled"
                                        size="sm"
                                      >
                                        {atrasadoCount} atrasado
                                        {atrasadoCount !== 1 ? 's' : ''}
                                      </Badge>
                                    )}
                                    {hasPendente && !hasAtrasado && (
                                      <Badge
                                        color="yellow"
                                        variant="filled"
                                        size="sm"
                                      >
                                        {pendenteCount} pendente
                                        {pendenteCount !== 1 ? 's' : ''}
                                      </Badge>
                                    )}
                                    {!hasAtrasado && !hasPendente && (
                                      <Badge
                                        color="green"
                                        variant="filled"
                                        size="sm"
                                      >
                                        Em dia
                                      </Badge>
                                    )}
                                  </>
                                )}
                              </Group>
                            </Group>
                          </Accordion.Control>
                          <Accordion.Panel>
                            <Stack spacing={8}>
                              <Group position="right" mb={4} spacing={8}>
                                <Tooltip
                                  label="Visualizar Termo de EPI"
                                  withArrow
                                >
                                  <Button
                                    size="xs"
                                    variant="subtle"
                                    color="gray"
                                    leftIcon={<IconFileText size={13} />}
                                    onClick={() =>
                                      handleOpenTermo(id, employeeName)
                                    }
                                  >
                                    Termo
                                  </Button>
                                </Tooltip>
                                <Tooltip
                                  label="Histórico de Entregas"
                                  withArrow
                                >
                                  <Button
                                    size="xs"
                                    variant="subtle"
                                    color="gray"
                                    leftIcon={<IconHistory size={13} />}
                                    onClick={() =>
                                      handleOpenHistorico(id, employeeName)
                                    }
                                  >
                                    Histórico
                                  </Button>
                                </Tooltip>
                                <Button
                                  size="xs"
                                  variant="light"
                                  color="teal"
                                  leftIcon={<IconLink size={13} />}
                                  onClick={() => {
                                    setLinkModal({
                                      employeeId: id,
                                      linkedEpiIds: epis.map((r) => r.epi.id),
                                    });
                                    setLinkEpiId(null);
                                    if (!catalog?.length) listCatalogFetcher();
                                  }}
                                >
                                  Vincular EPI
                                </Button>
                              </Group>
                              {epis.length === 0 ? (
                                <Text
                                  size="sm"
                                  color="dimmed"
                                  align="center"
                                  py={16}
                                >
                                  Nenhum EPI vinculado a este funcionário.
                                </Text>
                              ) : (
                                epis.map(
                                  (record: GroupedEpiRecord, rIdx: number) => {
                                    const status = statuses[rIdx];
                                    const lastDate =
                                      record.lastDeliveredAt ?? null;
                                    return (
                                      <Group
                                        key={record.id}
                                        position="apart"
                                        px={12}
                                        py={10}
                                        sx={(theme) => ({
                                          border: `1px solid ${theme.colors.gray[3]}`,
                                          borderRadius: theme.radius.sm,
                                          backgroundColor: theme.white,
                                        })}
                                      >
                                        <div>
                                          <Group spacing={8} mb={2}>
                                            <Text size="sm" fw={500}>
                                              {record.epi?.name ?? '—'}
                                            </Text>
                                            {record.epi?.category && (
                                              <Badge
                                                color="blue"
                                                variant="light"
                                                size="xs"
                                              >
                                                {record.epi.category}
                                              </Badge>
                                            )}
                                          </Group>
                                          {(record.epi?.brand ||
                                            record.epi?.ca) && (
                                            <Text size="xs" color="dimmed">
                                              {[
                                                record.epi?.brand,
                                                record.epi?.ca
                                                  ? `CA ${record.epi.ca}`
                                                  : null,
                                              ]
                                                .filter(Boolean)
                                                .join(' · ')}
                                            </Text>
                                          )}
                                          <Text
                                            size="xs"
                                            color={lastDate ? 'teal' : 'dimmed'}
                                            fw={lastDate ? 500 : undefined}
                                          >
                                            {lastDate
                                              ? `Entregue em ${formatLocale(
                                                  lastDate,
                                                  'DD/MM/YYYY',
                                                )}`
                                              : 'Sem entrega registrada'}
                                          </Text>
                                        </div>
                                        <Group spacing={8}>
                                          <StatusBadge status={status} />
                                          <Button
                                            size="xs"
                                            variant="light"
                                            color="blue"
                                            onClick={() =>
                                              setDeliverModalId(record.id)
                                            }
                                          >
                                            Marcar Entregue
                                          </Button>
                                          <Tooltip
                                            label="Desvincular EPI"
                                            withArrow
                                          >
                                            <ActionIcon
                                              size="sm"
                                              variant="subtle"
                                              color="red"
                                              onClick={() =>
                                                handleUnlinkEpi(record.id)
                                              }
                                            >
                                              <IconTrash size={14} />
                                            </ActionIcon>
                                          </Tooltip>
                                        </Group>
                                      </Group>
                                    );
                                  },
                                )
                              )}
                            </Stack>
                          </Accordion.Panel>
                        </Accordion.Item>
                      );
                    },
                  )}
                </Accordion>

                {totalEmployeePages > 1 && (
                  <Group position="center" mt={8}>
                    <Pagination
                      total={totalEmployeePages}
                      page={employeePage}
                      onChange={(p) => {
                        setEmployeePage(p);
                        listEpisGroupedFetcher({
                          employeeId: filterEmployeeId
                            ? Number(filterEmployeeId)
                            : undefined,
                          epiId: filterEpiId ? Number(filterEpiId) : undefined,
                          sector: filterSector || undefined,
                          role: filterRole || undefined,
                          hasNoEpi: filterNoEpi || undefined,
                          startDate: filterStartDate
                            ? filterStartDate.toISOString()
                            : undefined,
                          endDate: filterEndDate
                            ? filterEndDate.toISOString()
                            : undefined,
                          page: p,
                          limit: EMPLOYEES_PER_PAGE,
                        });
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    />
                  </Group>
                )}
              </>
            )}
          </Tabs.Panel>

          {/* -------- PRAZOS -------- */}
          <Tabs.Panel value="prazos" pt={16}>
            {deadlinesLoading ? (
              <Group position="center" py={48}>
                <Loader />
              </Group>
            ) : deadlineItems.length === 0 ? (
              <Stack align="center" py={48} spacing={8}>
                <IconShieldCheck size={40} color="#adb5bd" />
                <Text fw="bold" color="dimmed">
                  Nenhum prazo de reposição encontrado
                </Text>
                <Text size="sm" color="dimmed">
                  EPIs com período de reposição e ao menos uma entrega
                  registrada aparecerão aqui.
                </Text>
              </Stack>
            ) : (
              <Stack spacing={0}>
                {renderDeadlineSection(
                  overdue,
                  `🔴 Atrasados (${overdue.length})`,
                  'red',
                )}
                {renderDeadlineSection(
                  thisWeek,
                  `🟠 Vencem esta semana (${thisWeek.length})`,
                  'orange',
                )}
                {renderDeadlineSection(
                  thisMonth,
                  `🔵 Vencem nos próximos 30 dias (${thisMonth.length})`,
                  'blue',
                )}
                {overdue.length === 0 &&
                  thisWeek.length === 0 &&
                  thisMonth.length === 0 && (
                    <Stack align="center" py={48} spacing={8}>
                      <IconShieldCheck size={40} color="#adb5bd" />
                      <Text fw="bold" color="dimmed">
                        Nenhum prazo crítico no momento
                      </Text>
                      <Text size="sm" color="dimmed">
                        Todos os EPIs estão com reposição em dia ou além de 30
                        dias.
                      </Text>
                    </Stack>
                  )}
              </Stack>
            )}
          </Tabs.Panel>

          {/* -------- PENDENTES -------- */}
          <Tabs.Panel value="pendentes" pt={16}>
            {renderEmployeeAccordion(
              pendentesEmployees,
              totalPendentesPages,
              pendentesPage,
              (p) => {
                setPendentesPage(p);
                listPendentesFetcher({
                  page: p,
                  limit: EMPLOYEES_PER_PAGE,
                  hasPendingDelivery: true,
                });
              },
              pendentesLoading,
              totalPendentes,
              'Nenhum funcionário com EPI pendente de primeira entrega.',
            )}
          </Tabs.Panel>
        </Tabs>
      </Stack>

      {/* -------- MODALS (shared across tabs) -------- */}
      <Modal
        title={
          <Group spacing={8}>
            <IconFileText size={18} />
            <Text fw={600}>
              Termo de EPI
              {termoModal ? ` — ${termoModal.employeeName}` : ''}
            </Text>
          </Group>
        }
        opened={termoModal !== null}
        onClose={handleCloseTermoModal}
        size="80%"
        centered
      >
        <Stack spacing={12}>
          <Group position="apart">
            <FileButton
              accept="application/pdf,image/png,image/jpeg,image/jpg"
              onChange={(file) => {
                if (file && termoModal) {
                  handleUploadSignedTermo(file, termoModal.employeeId);
                }
              }}
            >
              {(props) => (
                <Button
                  {...props}
                  size="xs"
                  variant="light"
                  color="teal"
                  leftIcon={<IconUpload size={14} />}
                  loading={uploadFilesLoading || addEmployeeFilesLoading}
                >
                  Enviar termo assinado
                </Button>
              )}
            </FileButton>
            <Button
              size="xs"
              variant="light"
              leftIcon={<IconDownload size={14} />}
              disabled={!termoModal?.blob}
              onClick={() => {
                if (termoModal?.blob) {
                  downloadFile(
                    termoModal.blob,
                    `termo-epi-${termoModal.employeeId}.pdf`,
                  );
                }
              }}
            >
              Baixar PDF
            </Button>
          </Group>
          <Divider />
          {termoModal?.loading ? (
            <Group position="center" py={48}>
              <Loader size="sm" />
              <Text size="sm" color="dimmed">
                Gerando documento...
              </Text>
            </Group>
          ) : termoModal?.pdfUrl ? (
            <iframe
              src={termoModal.pdfUrl}
              style={{ width: '100%', height: 520, border: 'none' }}
              title="Pré-visualização do Termo"
            />
          ) : null}
        </Stack>
      </Modal>

      <Modal
        title={
          <Group spacing={8}>
            <IconHistory size={18} />
            <Text fw={600}>
              Histórico de Entregas
              {historyModal ? ` — ${historyModal.employeeName}` : ''}
            </Text>
          </Group>
        }
        opened={historyModal !== null}
        onClose={() => setHistoryModal(null)}
        size="xl"
        centered
      >
        <Stack spacing={12}>
          <Group position="right">
            <Button
              size="xs"
              variant="light"
              leftIcon={<IconDownload size={14} />}
              onClick={() =>
                historyModal && handleDownloadHistorico(historyModal.employeeId)
              }
            >
              Baixar PDF
            </Button>
          </Group>
          <Divider />
          {deliveriesLoading ? (
            <Group position="center" py={32}>
              <Loader size="sm" />
            </Group>
          ) : !deliveries?.items?.length ? (
            <Text size="sm" color="dimmed" align="center" py={24}>
              Nenhuma entrega registrada para este funcionário.
            </Text>
          ) : (
            <ScrollArea>
              <Table striped highlightOnHover fontSize="sm">
                <thead>
                  <tr>
                    <th>EPI</th>
                    <th>CA</th>
                    <th>Qtd</th>
                    <th>Motivo</th>
                    <th>Data</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveries.items.map((d) => (
                    <tr key={d.id}>
                      <td>{d.epi?.name ?? '—'}</td>
                      <td>{d.epi?.ca ? `CA ${d.epi.ca}` : '—'}</td>
                      <td>{d.quantity ?? 1}</td>
                      <td>
                        {d.reason
                          ? EpiDeliveryReasonLabel[d.reason] ?? d.reason
                          : '—'}
                      </td>
                      <td>{formatLocale(d.createdAt, 'DD/MM/YYYY HH:mm')}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </ScrollArea>
          )}
        </Stack>
      </Modal>

      <Modal
        title="Vincular EPI ao Funcionário"
        opened={linkModal !== null}
        onClose={() => {
          setLinkModal(null);
          setLinkEpiId(null);
        }}
        size="sm"
      >
        <Select
          label="Selecione o EPI"
          placeholder="busque pelo nome do EPI"
          searchable
          clearable
          data={(catalog ?? [])
            .filter((epi) => !linkModal?.linkedEpiIds.includes(epi.id))
            .map((epi) => ({ value: String(epi.id), label: epi.name }))}
          value={linkEpiId}
          onChange={setLinkEpiId}
          mb={20}
        />
        <Group position="right">
          <Button
            variant="outline"
            onClick={() => {
              setLinkModal(null);
              setLinkEpiId(null);
            }}
          >
            Cancelar
          </Button>
          <Button
            leftIcon={<IconLink size={14} />}
            loading={linkLoading}
            disabled={!linkEpiId}
            onClick={handleLinkEpi}
          >
            Vincular
          </Button>
        </Group>
      </Modal>

      <Modal
        title="Registrar Entrega de EPI"
        opened={deliverModalId !== null}
        onClose={() => {
          setDeliverModalId(null);
          deliverForm.reset();
        }}
        size="sm"
      >
        <form
          onSubmit={deliverForm.onSubmit((values) => {
            if (deliverModalId === null) return;
            handleDeliver(deliverModalId, {
              reason: values.reason || undefined,
              quantity: values.quantity,
              deliveredAt: values.deliveredAt,
            });
          })}
        >
          <CustomDatePicker
            label="Data da entrega"
            placeholder="selecione a data"
            required
            mb={12}
            {...deliverForm.getInputProps('deliveredAt')}
          />
          <Select
            label="Motivo da entrega"
            placeholder="selecione o motivo"
            clearable
            data={EpiDeliveryReasonOptions}
            mb={12}
            {...deliverForm.getInputProps('reason')}
          />
          <NumberInput
            label="Quantidade"
            min={1}
            mb={20}
            {...deliverForm.getInputProps('quantity')}
          />
          <Group position="right">
            <Button
              variant="outline"
              onClick={() => {
                setDeliverModalId(null);
                deliverForm.reset();
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" loading={deliverLoading}>
              Confirmar Entrega
            </Button>
          </Group>
        </form>
      </Modal>

      <Drawer
        opened={catalogOpen}
        onClose={() => setCatalogOpen(false)}
        position="right"
        size="80%"
        padding="lg"
        title={null}
        styles={{
          drawer: { display: 'flex', flexDirection: 'column' },
          body: { flex: 1, overflow: 'auto', padding: '24px' },
          header: { display: 'none' },
        }}
      >
        <EpiCatalogPanel onSaved={loadData} />
      </Drawer>
    </Page>
  );
}
